import { GpmPayConfigError } from './errors.js';
import { assertApiToken } from './token.js';

export const DEFAULT_BASE_URL = 'https://api.gpmpay.com';
export const SANDBOX_BASE_URL = 'https://sandbox-api.gpmpay.com';
export const DEFAULT_TIMEOUT_MS = 30_000;
export const DEFAULT_MAX_RETRIES = 2;
export const DEFAULT_RETRY_BASE_DELAY_MS = 500;

export interface RequestLogEvent {
  method: string;
  url: string;
  requestId: string;
  attempt: number;
}

export interface ResponseLogEvent {
  requestId: string;
  status: number;
  durationMs: number;
  attempt: number;
}

export interface GpmPayOptions {
  /**
   * **Required.** Your GPM Pay API token (`gpm_…`).
   *
   * Create one at https://app.gpmpay.com/api-tokens. This is a
   * server-side secret — never ship it to a browser or mobile client.
   */
  apiToken: string;

  /**
   * API base URL. Accepts a bare host, `host/api`, or `host/api/v1` — the SDK
   * normalizes all of them. Defaults to `https://api.gpmpay.com`.
   */
  baseUrl?: string;

  /** Shorthand for the sandbox host. Ignored when `baseUrl` is set. */
  sandbox?: boolean;

  /** Per-request timeout in ms. Default 30000. */
  timeoutMs?: number;

  /** Retries for retryable failures. Default 2. Set 0 to disable. */
  maxRetries?: number;

  /** Base backoff in ms; full-jitter exponential capped at 8s. Default 500. */
  retryBaseDelayMs?: number;

  /** Inject a fetch implementation (tests, proxy agents, instrumentation). */
  fetch?: typeof globalThis.fetch;

  /** Appended to the SDK's own User-Agent. */
  userAgent?: string;

  /** Merged into every request. Cannot override `Authorization`. */
  defaultHeaders?: Record<string, string>;

  /**
   * Reject tokens that don't match the documented `gpm_…` shape. Default true.
   * The presence check always applies regardless of this flag.
   */
  strictTokenFormat?: boolean;

  /** Observability hooks. Never receive the token. */
  onRequest?: (event: RequestLogEvent) => void;
  onResponse?: (event: ResponseLogEvent) => void;
}

export interface ResolvedConfig {
  apiToken: string;
  baseUrl: string;
  timeoutMs: number;
  maxRetries: number;
  retryBaseDelayMs: number;
  fetchImpl: typeof globalThis.fetch;
  userAgent: string | undefined;
  defaultHeaders: Record<string, string>;
  onRequest: ((event: RequestLogEvent) => void) | undefined;
  onResponse: ((event: ResponseLogEvent) => void) | undefined;
}

/**
 * Normalize any reasonable spelling of the API host into a full versioned base.
 *
 * Mirrors the WooCommerce plugin's normalization so merchants can paste
 * whatever they have. `http://` is preserved — localhost must not be forced
 * to https.
 *
 * @example
 * normalizeBaseUrl('api.gpmpay.com')              // https://api.gpmpay.com/api/v1
 * normalizeBaseUrl('https://api.gpmpay.com/')     // https://api.gpmpay.com/api/v1
 * normalizeBaseUrl('https://api.gpmpay.com/api')  // https://api.gpmpay.com/api/v1
 * normalizeBaseUrl('http://localhost:4000')       // http://localhost:4000/api/v1
 */
export function normalizeBaseUrl(input: string): string {
  if (typeof input !== 'string') {
    throw new GpmPayConfigError('baseUrl must be a string.', 'invalid_base_url');
  }

  let base = input.trim().replace(/\/+$/, '');
  if (base === '') {
    throw new GpmPayConfigError('baseUrl is empty.', 'invalid_base_url');
  }
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(base)) {
    base = `https://${base}`;
  }

  let parsed: URL;
  try {
    parsed = new URL(base);
  } catch {
    throw new GpmPayConfigError(
      `baseUrl is not a valid URL: ${input}`,
      'invalid_base_url',
    );
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new GpmPayConfigError(
      `baseUrl must use http or https, got "${parsed.protocol}".`,
      'invalid_base_url',
    );
  }

  if (/\/api\/v\d+$/.test(base)) return base;
  if (/\/api$/.test(base)) return `${base}/v1`;
  return `${base}/api/v1`;
}

function resolveFetch(injected?: typeof globalThis.fetch): typeof globalThis.fetch {
  if (injected) return injected;
  if (typeof globalThis.fetch === 'function') {
    return globalThis.fetch.bind(globalThis);
  }
  throw new GpmPayConfigError(
    'global fetch is unavailable. @gpmpay/sdk requires Node 18.17+, ' +
      'or pass a fetch implementation via { fetch }.',
    'invalid_argument',
  );
}

export function resolveConfig(options: GpmPayOptions): ResolvedConfig {
  if (options === undefined || options === null) {
    // Reaching assertApiToken with `undefined` produces the actionable message.
    return resolveConfig({ apiToken: undefined as unknown as string });
  }

  const apiToken = assertApiToken(
    options.apiToken,
    options.strictTokenFormat ?? true,
  );

  const rawBase =
    options.baseUrl ?? (options.sandbox === true ? SANDBOX_BASE_URL : DEFAULT_BASE_URL);

  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    throw new GpmPayConfigError(
      `timeoutMs must be a positive number, got ${String(options.timeoutMs)}.`,
      'invalid_argument',
    );
  }

  const maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES;
  if (!Number.isInteger(maxRetries) || maxRetries < 0) {
    throw new GpmPayConfigError(
      `maxRetries must be a non-negative integer, got ${String(options.maxRetries)}.`,
      'invalid_argument',
    );
  }

  // Authorization is applied after these, so it can never be overridden here.
  const defaultHeaders: Record<string, string> = {};
  for (const [key, value] of Object.entries(options.defaultHeaders ?? {})) {
    if (key.toLowerCase() === 'authorization') continue;
    defaultHeaders[key] = value;
  }

  return {
    apiToken,
    baseUrl: normalizeBaseUrl(rawBase),
    timeoutMs,
    maxRetries,
    retryBaseDelayMs: options.retryBaseDelayMs ?? DEFAULT_RETRY_BASE_DELAY_MS,
    fetchImpl: resolveFetch(options.fetch),
    userAgent: options.userAgent,
    defaultHeaders,
    onRequest: options.onRequest,
    onResponse: options.onResponse,
  };
}
